<template>
    <div class="">
        <div class="ppv2-py-4">
            <DataTable
                :perPage="itemsPerPage"
                :columns="tableColumns"
                :items="categories.data"
                :currentPage="currentPage"
                :totalPages="totalPages"
                :selectedItems="selectedItems"
                :enableDragDrop="true"
                :enableMultiSelect="false"
                @page-changed="handlePageChange"
                @item-selected="toggleItemSelection"
                @select-all-toggled="toggleSelectAll"
                @sort="onSort"
            >
                <!-- Toolbar Slot -->
                <template #toolbar>
                    <!-- Breadcrumb Navigation -->
                    <div v-if="breadcrumbs.length > 0" class="ppv2-flex ppv2-items-center ppv2-gap-2 ppv2-mb-4">
                        <span
                            class="ppv2-text-[#7D2DBD] ppv2-cursor-pointer poppinsSemiBold ppv2-text-2xl hover:ppv2-underline"
                            @click="navigateToRoot"
                        >
                            {{ $t('food.navigation.categories') || 'Catégories' }}
                        </span>
                        <span
                            v-for="(crumb, index) in breadcrumbs"
                            :key="crumb.id"
                            class="ppv2-flex ppv2-items-center ppv2-gap-2"
                        >
                            <ChevronRight :size="20" class="ppv2-text-gray-400" />
                            <span
                                class="poppinsSemiBold ppv2-text-2xl"
                                :class="
                                    index === breadcrumbs.length - 1
                                        ? 'ppv2-text-black'
                                        : 'ppv2-text-[#7D2DBD] ppv2-cursor-pointer hover:ppv2-underline'
                                "
                                @click="index < breadcrumbs.length - 1 ? navigateToBreadcrumb(index) : null"
                            >
                                {{ crumb.name }}
                            </span>
                        </span>
                    </div>

                    <div class="ppv2-flex ppv2-flex-wrap ppv2-items-center" :style="{ gap: normalizeSize(16, true) }">
                        <!-- Search Input -->
                        <div class="ppv2-relative">
                            <Search v-model="searchQuery" :emitGlobal="false" @search-updated="handleSearchUpdate" />
                        </div>

                        <div class="ppv2-flex-1"></div>

                        <!-- Create Button -->
                        <BaseButton
                            variant="purple"
                            :custom-height="40"
                            :custom-font-size="14"
                            custom-class="ppv2-gap-3 ppv2-pl-4 ppv2-pr-[20px]"
                            @click="navigateToAdd"
                        >
                            <Plus :size="17" />
                            <span class="poppinsRegular">
                                {{ $t('experiences.actions.create') }}
                            </span>
                        </BaseButton>
                    </div>
                </template>

                <!-- Column: Image -->
                <template #column-image="{ item }">
                    <div class="ppv2-w-10 ppv2-h-10 ppv2-rounded-full ppv2-overflow-hidden ppv2-border ppv2-border-gray-200">
                        <img :src="item.image" alt="Image" class="ppv2-w-full ppv2-h-full ppv2-object-cover" v-if="item.image" />
                        <div
                            v-else
                            class="ppv2-w-full ppv2-h-full ppv2-bg-gray-100 ppv2-flex ppv2-items-center ppv2-justify-center"
                        >
                            <span class="ppv2-text-xs ppv2-text-gray-400">N/A</span>
                        </div>
                    </div>
                </template>

                <!-- Column: Name -->
                <template #column-name="{ item }">
                    <span
                        :class="{ 'ppv2-cursor-pointer hover:ppv2-underline': item.children_count > 0 }"
                        @click="item.children_count > 0 ? navigateToCategory(item) : null"
                    >
                        {{ item.name }}
                    </span>
                </template>

                <!-- Column: Children Count -->
                <template #column-children_count="{ item }">
                    <a
                        href="#"
                        class="ppv2-underline ppv2-text-black"
                        @click.prevent="item.children_count > 0 ? navigateToCategory(item) : null"
                        :class="{ 'ppv2-cursor-default': item.children_count === 0 }"
                    >
                        {{ item.children_count }}
                    </a>
                </template>

                <!-- Column: Visibility -->
                <template #column-is_visible="{ item }">
                    <button
                        @click="toggleVisibility(item)"
                        class="ppv2-min-w-[125px] ppv2-inline-flex ppv2-items-center ppv2-gap-2 ppv2-px-3 ppv2-py-2 ppv2-rounded-xl ppv2-border ppv2-border-solid ppv2-transition-all"
                        :class="
                            item.is_visible
                                ? 'ppv2-border-[#206A64] ppv2-bg-[#06B217] ppv2-bg-opacity-20'
                                : 'ppv2-border-[#4E4E4E] ppv2-bg-[#474747] ppv2-bg-opacity-20'
                        "
                    >
                        <div
                            class="ppv2-w-9 ppv2-h-5 ppv2-rounded-full ppv2-flex ppv2-items-center ppv2-transition-all"
                            :class="
                                item.is_visible ? 'ppv2-bg-[#206A64] ppv2-justify-end' : 'ppv2-bg-[#4E4E4E] ppv2-justify-start'
                            "
                        >
                            <div class="ppv2-w-3.5 ppv2-h-3.5 ppv2-bg-white ppv2-rounded-full ppv2-mx-0.5"></div>
                        </div>
                        <span class="ppv2-text-black poppinsRegular" :style="{ fontSize: normalizeFontSize(14) }">
                            {{ item.is_visible ? $t('experiences.status.published') : $t('experiences.status.draft') }}
                        </span>
                    </button>
                </template>

                <!-- Column: Actions -->
                <template #column-actions="{ item }">
                    <div
                        class="ppv2-flex ppv2-items-center ppv2-justify-center ppv2-gap-3"
                        :style="{ fontSize: normalizeFontSize(14) }"
                    >
                        <a
                            :href="`/back-office/v3/tag/edit/${item.id}`"
                            class="ppv2-p-1 ppv2-text-black hover:ppv2-bg-black hover:ppv2-bg-opacity-10 ppv2-rounded ppv2-transition-all"
                            :title="$t('experiences.actions.edit')"
                        >
                            <SquarePen :size="18" />
                        </a>
                        <button
                            class="btnTrash ppv2-p-1 hover:ppv2-bg-[#FF9C9C] hover:ppv2-bg-opacity-10 ppv2-rounded ppv2-transition-all"
                            :title="$t('experiences.actions.delete')"
                            @click="deleteSingleItem(item)"
                        >
                            <Trash2 :size="18" />
                        </button>
                    </div>
                </template>

                <!-- Bulk Actions Slot -->
                <template #bulk-actions="{ selectedCount }">
                    <span class="poppinsRegular textGray" :style="{ fontSize: normalizeFontSize(15) }">
                        {{ selectedCount }} élément{{ selectedCount > 1 ? 's' : '' }} sélectionné{{
                            selectedCount > 1 ? 's' : ''
                        }}
                    </span>
                    <span class="poppinsRegular ppv2-text-white ppv2-text-opacity-50" :style="{ fontSize: normalizeFontSize(15) }"
                        >|</span
                    >
                    <BaseButton
                        variant="outline"
                        @click="deleteSelectedItems"
                        customClass="!ppv2-px-4 !ppv2-h-[30px] !ppv2-flex !ppv2-items-center !ppv2-gap-2"
                    >
                        <Trash2 :size="14" />
                        <span :style="{ fontSize: normalizeFontSize(14) }">{{ $t('common.deleteSelection') }}</span>
                    </BaseButton>
                </template>
            </DataTable>
        </div>

        <!-- Delete Confirmation Popup -->
        <CustomDialog v-model="showDeletePopup" :defaultTitle="deletePopupTitle" customClassTitle="poppinsSemiBold" noScroll>
            <template #content>
                <p class="ppv2-text-center ppv2-text-black poppinsRegular" :style="{ fontSize: normalizeFontSize(15) }">
                    {{ deletePopupDescription }}
                </p>
            </template>
            <template #actions>
                <BaseButton
                    variant="outline"
                    @click="showDeletePopup = false"
                    customClass="!ppv2-min-h-[40px] !ppv2-text-base !ppv2-min-w-[170px]"
                    :customHeight="45"
                >
                    {{ $t('experiences.deletePopup.cancel') }}
                </BaseButton>
                <BaseButton
                    variant="danger"
                    @click="confirmDelete"
                    customClass="!ppv2-min-h-[40px] !ppv2-text-base !ppv2-min-w-[170px]"
                    :customHeight="45"
                >
                    {{ $t('tag.deletePopupConfirm') }}
                </BaseButton>
            </template>
        </CustomDialog>
    </div>
</template>

<script>
import { Plus, Trash2, SquarePen, ListTree, ChevronRight } from 'lucide-vue'
import normalizeSize from '../../../utils/normalizeSize'
import Search from './components/Search.vue'
import BaseButton from '../../Shared/BaseButton.vue'
import CustomDialog from '../../Shared/CustomDialog.vue'
import DataTable from '../../Shared/DataTable.vue'
import { eventBus } from '../../Customer/eventBus'
import axios from 'axios'
import normalizeFontSize from '../../../utils/normalizeFontSize'
import { getPerPage, setPerPage } from '../../../utils/perPage'

export default {
    name: 'PrestationCategoriesTable',
    components: {
        Plus,
        SquarePen,
        Trash2,
        ListTree,
        Search,
        BaseButton,
        CustomDialog,
        DataTable,
    },
    data() {
        return {
            searchQuery: '',
            currentPage: 1,
            itemsPerPage: getPerPage('prestation_categories_items_per_page', 10),
            selectedItems: [],
            isLoading: false,
            error: null,
            tableColumns: [
                { key: 'image', label: this.$t('experiences.table.imageTag') || 'Image', sortable: false, type: 'custom' },
                { key: 'name', label: this.$t('experiences.table.name'), sortable: true, type: 'custom' },
                {
                    key: 'children_count',
                    label: this.$t('food.categories.columns.subcategories_count') || 'Nbr. sous-catégories',
                    sortable: true,
                    type: 'custom',
                },
                { key: 'prestations_count', label: this.$t('tag.experiencesCount'), sortable: true, type: 'text' },
                { key: 'packages_count', label: this.$t('tag.combosCount'), sortable: true, type: 'text' },
                { key: 'is_visible', label: this.$t('experiences.table.state'), sortable: true, type: 'badge' },
                { key: 'actions', label: this.$t('experiences.table.actions'), sortable: false, type: 'actions' },
            ],
            showDeletePopup: false,
            deletePopupTitle: this.$t('experiences.deletePopup.title'),
            deletePopupDescription: '',
            deleteAction: null, // 'single' or 'multiple'
            itemToDelete: null,
            categories: {
                data: [],
                total: 0,
            },
            sortKey: 'id',
            sortOrder: 'desc',
            breadcrumbs: [],
            currentParentId: null,
        }
    },
    computed: {
        totalPages() {
            return this.categories.total
        },
    },
    mounted() {
        eventBus.$on('dt-selected-perpage-changed', this.handlePerPageChange)
        this.fetchCategories()
    },
    beforeDestroy() {
        eventBus.$off('dt-selected-perpage-changed', this.handlePerPageChange)
    },
    methods: {
        normalizeFontSize,
        normalizeSize,
        async fetchCategories() {
            this.isLoading = true
            try {
                const response = await axios.get('/back-office/v3/tag/list', {
                    params: {
                        currentPage: this.currentPage,
                        perPage: this.itemsPerPage,
                        search: this.searchQuery,
                        orderBy: this.sortKey,
                        orderDirection: this.sortOrder,
                        parent_id: this.currentParentId,
                    },
                })
                this.categories = {
                    data: response.data.data,
                    total: response.data.last_page,
                }
            } catch (error) {
                this.$toast.error(error.response.data.message)
            } finally {
                this.isLoading = false
            }
        },
        async toggleVisibility(item) {
            const previousVisibility = item.is_visible
            item.is_visible = !item.is_visible
            try {
                await axios.patch('/back-office/v3/tag/update-visibility', {
                    id: item.id,
                    is_visible: item.is_visible,
                })
                this.$toast?.success(this.$t('tag.updatedStatus') || 'Tag mis à jour')
                this.$emit('refresh-stats')
            } catch (error) {
                this.$toast.error(error.response.data.message)
                item.is_visible = previousVisibility
                console.error('Error updating visibility:', error)
            }
        },
        handlePageChange(page) {
            this.currentPage = page
            this.fetchCategories()
        },
        handlePerPageChange(perPage) {
            this.itemsPerPage = Number(perPage)
            setPerPage('prestation_categories_items_per_page', this.itemsPerPage)
            this.currentPage = 1
            this.fetchCategories()
        },
        handleSearchUpdate(searchValue) {
            this.searchQuery = searchValue
            this.currentPage = 1
            clearTimeout(this._searchDebounce)
            this._searchDebounce = setTimeout(() => {
                this.fetchCategories()
            }, 350)
        },
        toggleItemSelection(itemId) {
            const index = this.selectedItems.indexOf(itemId)
            if (index > -1) {
                this.selectedItems.splice(index, 1)
            } else {
                this.selectedItems.push(itemId)
            }
        },
        toggleSelectAll() {
            if (this.selectedItems.length === this.categories.data.length) {
                this.selectedItems = []
            } else {
                this.selectedItems = this.categories.data.map((item) => item.id)
            }
        },
        deleteSingleItem(item) {
            this.deleteAction = 'single'
            this.itemToDelete = item
            this.deletePopupDescription = this.$t('experiences.deletePopup.message')
            this.showDeletePopup = true
        },
        deleteSelectedItems() {
            this.deleteAction = 'multiple'
            this.deletePopupDescription = this.$t('experiences.deletePopup.message')
            this.showDeletePopup = true
        },
        async confirmDelete() {
            try {
                if (this.deleteAction === 'single') {
                    const response = await axios.delete(`/back-office/v3/tag/delete/${this.itemToDelete.id}`)
                    if (response.data.success) {
                        this.$toast?.success(this.$t('tag.deleteSuccess') || 'Supprimé avec succès')
                        this.fetchCategories()
                    }
                } else if (this.deleteAction === 'multiple' && this.selectedItems.length > 0) {
                    for (const itemId of this.selectedItems) {
                        await axios.delete(`/back-office/v3/tag/delete/${itemId}`)
                    }
                    this.$toast?.success(this.$t('tag.deleteSuccess') || 'Supprimé avec succès')
                    this.selectedItems = []
                    this.fetchCategories()
                }
                this.$emit('refresh-stats')
            } catch (error) {
                this.$toast.error(error.response.data.message)
                console.error('Error deleting category:', error)
                if (error.response && error.response.data && error.response.data.message) {
                    this.$toast?.error(error.response.data.message)
                }
            } finally {
                this.showDeletePopup = false
                this.deleteAction = null
                this.itemToDelete = null
            }
        },
        onSort(key) {
            if (this.sortKey === key) {
                this.sortOrder = this.sortOrder === 'asc' ? 'desc' : 'asc'
            } else {
                this.sortKey = key
                this.sortOrder = 'asc'
            }
            this.currentPage = 1
            this.fetchCategories()
        },
        navigateToCategory(item) {
            this.currentParentId = item.id
            this.breadcrumbs.push({
                id: item.id,
                name: item.name,
            })
            this.currentPage = 1
            this.$emit('breadcrumb-change', true)
            this.fetchCategories()
        },
        navigateToRoot() {
            this.currentParentId = null
            this.breadcrumbs = []
            this.currentPage = 1
            this.$emit('breadcrumb-change', false)
            this.fetchCategories()
        },
        navigateToBreadcrumb(index) {
            this.breadcrumbs = this.breadcrumbs.slice(0, index + 1)
            const lastCrumb = this.breadcrumbs[this.breadcrumbs.length - 1]
            this.currentParentId = lastCrumb.id
            this.currentPage = 1
            this.$emit('breadcrumb-change', true)
            this.fetchCategories()
        },
        navigateToAdd() {
            window.location.href = '/back-office/v3/tag/create'
        },
    },
}
</script>

<style scoped lang="scss">
.textGray {
    color: #1a1f37;
}

.btnTrash {
    color: #911414;
}

.dark {
    .textGray {
        color: #bfbfbf;
    }

    .btnTrash {
        color: #ff9c9c;
    }
}
</style>
